import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { useGet } from './templateHooks';
import { FAST_API_URL } from 'src/config';
import {
  CreateExport,
  TriggerListTagging,
  PaginatedExports,
  ExportResponse,
  ExportDownloadResponse,
} from 'src/@types/export';

export const useCreateExport = () => {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<ExportResponse, CreateExport>({
    endpoint: '/v2/exports',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exports'] });
    },
  });
};

export const useCreateListTagging = () => {
  const mutationFn = useAxios<unknown, CreateExport>({
    endpoint: '/v1/list-tagging',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn });
};

export const useExports = (
  config = {},
  params: {
    all_exports?: boolean;
    order_by?: string;
    order?: string;
    page?: number;
    per_page?: number;
  } = {}
) =>
  useGet<
    PaginatedExports,
    { all_exports?: boolean; order_by?: string; order?: string; page?: number; per_page?: number }
  >({
    queryKey: ['exports'],
    endpoint: '/v2/exports',
    config: config,
    params: params,
    url: FAST_API_URL,
  });

export const useTriggerTaggingFromExport = (resourceId: string) => {
  const mutationFn = useAxios<undefined, TriggerListTagging>({
    endpoint: '/v1/exports/' + resourceId + '/bulk-tag',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn });
};

export const useDownloadExport = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const fetchDownloadUrl = useAxios<ExportDownloadResponse>({
    endpoint: '/v1/exports',
    method: 'get',
    url: FAST_API_URL,
  });

  const download = async (exportId: string) => {
    const newTab = window.open('', '_blank');
    setIsDownloading(true);
    try {
      const response = await fetchDownloadUrl({ resourceId: `${exportId}/download` });
      if (newTab) {
        newTab.location.href = response.url;
      } else {
        window.location.href = response.url;
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading };
};
